// Чистые функции экранов ИИ: подписи дней, целей упражнений, кардио, периодов отчётов.

import { formatDate, formatNumber } from '@/lib/format'

import type { CardioSpec, Proposal, ProposalExercise, Report } from './api'

export const WEEKDAYS = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'] as const

export function weekdayLabel(weekday: number): string {
  return WEEKDAYS[weekday] ?? `День ${weekday}`
}

export const EQUIPMENT_LABELS: Record<string, string> = {
  barbell: 'Штанга',
  dumbbell: 'Гантели',
  smith: 'Тренажёр Смита',
  machine: 'Тренажёры',
  cable: 'Блоки',
  bodyweight: 'Свой вес',
  kettlebell: 'Гиря',
  band: 'Резинки',
  other: 'Другое',
}

const SPORTS: Record<string, string> = {
  run: 'Бег',
  bike: 'Велосипед',
  walk: 'Ходьба',
  swim: 'Плавание',
  other: 'Кардио',
}

/** «1 мин 30 с», «2 мин», «45 с». */
export function shortDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s} с`
  const m = Math.floor(s / 60)
  const rest = s % 60
  if (m >= 60 && rest === 0 && m % 60 === 0) return `${m / 60} ч`
  return rest ? `${m} мин ${rest} с` : `${m} мин`
}

function distance(m: number): string {
  return m >= 1000 ? `${formatNumber(m / 1000)} км` : `${formatNumber(m, { digits: 0 })} м`
}

/** «вместо «Жим штанги горизонтально» · Jev 94%» — упражнение подобрал Jev; иначе null. */
export function matchNote(ex: ProposalExercise): string | null {
  if (ex.match_confidence == null || !ex.exercise_name) return null
  return `вместо «${ex.name}» · Jev ${Math.round(ex.match_confidence * 100)}%`
}

/** «3 × 5 × 62,5 кг · отдых 2 мин». */
export function exerciseTargets(ex: ProposalExercise): string {
  let one: string
  if (ex.tracking === 'time' && ex.duration_s) one = shortDuration(ex.duration_s)
  else if (ex.tracking === 'distance' && ex.distance_m) one = distance(ex.distance_m)
  else if (ex.reps) one = String(ex.reps)
  else if (ex.duration_s) one = shortDuration(ex.duration_s)
  else if (ex.distance_m) one = distance(ex.distance_m)
  else one = '?'
  let text = `${ex.sets} × ${one}`
  if (ex.weight_kg) text += ` × ${formatNumber(ex.weight_kg)} кг`
  if (ex.rest_s) text += ` · отдых ${shortDuration(ex.rest_s)}`
  return text
}

/** «Бег · 30 мин · зона 2». */
export function cardioText(cardio: CardioSpec): string {
  const parts = [SPORTS[cardio.sport] ?? 'Кардио']
  if (cardio.duration_s) parts.push(shortDuration(cardio.duration_s))
  if (cardio.distance_m) parts.push(distance(cardio.distance_m))
  if (cardio.hr_zone) parts.push(`зона ${cardio.hr_zone}`)
  return parts.join(' · ')
}

export function canApply(p: Proposal): boolean {
  return p.status === 'pending' && p.proposal !== null && p.issues.length === 0
}

export const PROPOSAL_STATUS: Record<Proposal['status'], { text: string; tone: 'neutral' | 'accent' | 'warning' | 'danger' }> = {
  pending: { text: 'Черновик', tone: 'warning' },
  applied: { text: 'Применён', tone: 'accent' },
  rejected: { text: 'Отклонён', tone: 'neutral' },
  invalid: { text: 'Не прошёл проверку', tone: 'danger' },
}

export function reportPeriod(r: Pick<Report, 'kind' | 'period_start' | 'period_end'>): string {
  if (r.period_start === r.period_end) return formatDate(r.period_start)
  return `${formatDate(r.period_start)} – ${formatDate(r.period_end)}`
}

const MONTHS = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
]

/** '2026-10' → «октябрь 2026». */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number)
  return y && m ? `${MONTHS[m - 1]} ${y}` : month
}

export function formatTokens(n: number): string {
  return formatNumber(n, { digits: 0 })
}
